import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardBody } from "reactstrap";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import DocumentPicker from "components/CRM/DocumentPicker";
import SettingsGroup from "components/CRM/SettingsGroup";
import useUser from "api/api-hooks/auth/useUser";
import TenantAPI from "api/api/TenantAPI";
import PubSub from "utils/PubSub";
import PushEvents from "utils/PushEvents";
import NameDescriptionGroup from "./NameDescriptionGroup";
import PageViewDocument from "components/PageView/PageViewDocument/PageViewDocument";
import AppDrawer from "components/Drawers/AppDrawer";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import usePage from "../../../api/api-hooks/pages/usePage";
import { currentViewAtom } from "../../../atoms/ViewAtoms";
import Constants from "../../../utils/Constants";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

const RichTextSubWidget = ({ documentID }) => {
  if (documentID === "new") {
    return <SkeletonLoader width={"100%"} height={100} />;
  }

  const { pageID } = useParams();
  const { page } = usePage(pageID);

  const view = useAtomValue(currentViewAtom);

  return (
    <PageViewDocument
      view={view}
      document={{ ModuleAPIName: "Documents", id: documentID }}
      page={page}
      useToolbar={true}
    />
  );
};

const DocumentWidgetSettings = ({
  open,
  setSettings,
  onClose,
  section,
  updateWidget,
  theme,
}) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const [widget, setWidget] = useState({ ...section });
  const [documentID, setDocumentID] = useState(null);

  const handleDocumentChange = (documentID) => {
    setDocumentID(documentID);

    if (documentID === "new") {
      let documentPayload = {
        Name: widget?.Name ? widget?.Name : "New Document",
        Owner: {
          ModuleAPIName: "Contacts",
          id: user?.id,
        },
      };

      TenantAPI.insertRecord("Documents", documentPayload)
        .then(
          (data) => {
            let record = data?.data?.data?.data;

            setDocumentID(record?.id);

            let newOne = widget;
            newOne.Document = { ModuleAPIName: "Documents", id: record?.id };

            PubSub.publish(PushEvents.DOCUMENT.CREATED, record);

            queryClient.invalidateQueries({
              queryKey: [tenantID, "records", "Documents"],
            });

            setWidget(newOne);
          },
          (e) => {
            console.error(e);
          }
        )
        .finally(() => {});
    } else {
      let newOne = widget;
      newOne.Document = { ModuleAPIName: "Documents", id: documentID };

      setWidget(newOne);
    }
  };

  const updateSection = (prop, value) => {
    setWidget((prev) => {
      prev[prop] = value;
      return { ...prev };
    });
  };

  const handleSave = () => {
    updateWidget(widget);
    onClose();
  };

  useEffect(() => {
    setWidget({ ...section });
    setDocumentID(section?.Document?.id ?? null);
  }, [section]);

  return (
    <AppDrawer
      size="full"
      placement="bottom"
      open={open}
      onClose={onClose}
      headerTitle={`${section?.Name} Settings`}
      bodyStyle={{ padding: 0 }}
      headerOptions={
        <Button color="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      }
      body={
        <div className="d-flex" style={{ height: "100%" }}>
          <div
            className="py-4 px-3"
            style={{
              width: "calc(100% - 350px)",
            }}
          >
            {documentID ? (
              <RichTextSubWidget documentID={documentID} />
            ) : (
              <>
                <Card className="border shadow-none">
                  <CardBody>
                    Select a Document in the Document Settings to continue.
                  </CardBody>
                </Card>
              </>
            )}
          </div>
          <div
            style={{ minWidth: "200px", maxWidth: "350px", overflowY: "auto" }}
            className="border-left p-4 w-100"
          >
            <NameDescriptionGroup
              widget={widget}
              updateSection={updateSection}
              theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
            />
            <SettingsGroup title="Document Settings">
              <DocumentPicker
                value={documentID}
                onChange={handleDocumentChange}
              />
            </SettingsGroup>
          </div>
        </div>
      }
    />
  );
};

export default DocumentWidgetSettings;
